/**
 * One value, held in the browser between visits.
 *
 * Every read and write is best-effort: private mode denies storage, a full
 * quota denies a write, and a payload written by an older deploy may no longer
 * parse. None of that is worth an error to the visitor — the surface falls
 * back to its empty state — so the failure is swallowed here rather than at
 * four call sites that each remembered a different half of it.
 *
 * `validate` is what keeps a stale payload from reaching the app: a store that
 * carries a version or a shape says so once, here, instead of leaving it to
 * whoever reads it next.
 */
export function browserStore<T>(
  key: string,
  {
    storage = 'local',
    validate,
  }: {
    /** `session` for what belongs to one visit — a summary handed to the next
     *  page, not a preference. */
    storage?: 'local' | 'session'
    validate?: (parsed: unknown) => boolean
  } = {},
) {
  const area = (): Storage | null => {
    try {
      const store =
        storage === 'local' ? window.localStorage : window.sessionStorage
      return store ?? null
    } catch {
      return null
    }
  }

  return {
    load(): T | null {
      try {
        const raw = area()?.getItem(key)
        if (!raw) return null
        const parsed: unknown = JSON.parse(raw)
        if (validate && !validate(parsed)) return null
        return parsed as T
      } catch {
        return null
      }
    },

    save(value: T): void {
      try {
        area()?.setItem(key, JSON.stringify(value))
      } catch {
        // Nothing to recover: the next load falls back to the empty state.
      }
    },
  }
}
